import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { routeForNotification } from "@/lib/notificationRoute";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bell, Key, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  const load = () => api.get("/notifications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); };

  const onOpen = (n, hasOwnAction) => {
    if (!n.read) markRead(n.id);
    if (hasOwnAction) return; // cihaz/şifre onayı gibi kendi butonu olanları yönlendirme
    const route = routeForNotification(n);
    if (route) navigate(route);
  };

  const decideDevice = async (approvalId, action) => {
    try {
      await api.post(`/auth/device-approval/${approvalId}`, { action });
      toast.success(action === "approve" ? "Giriş onaylandı — yeni cihazınız aktif" : "Giriş reddedildi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem yapılamadı");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Etkinlik</div>
        <h1 className="font-display text-3xl font-bold mt-1">Bildirimler</h1>
      </div>
      <div className="space-y-2">
        {items.map((n) => {
          const isPwdReq = n.type === "hr_password_change_request" && n.token;
          const isPwdReset = n.type === "password_reset_request" && n.reset_url;
          const isDeviceReq = n.type === "device_approval_request" && n.approval_id;
          const icon = isDeviceReq ? <ShieldCheck size={18} className="text-emerald-500 mt-0.5" /> :
                       (isPwdReq || isPwdReset) ? <Key size={18} className="text-amber-500 mt-0.5" /> :
                       <Bell size={18} className="text-accent mt-0.5" />;
          const hasOwnAction = isPwdReq || isPwdReset || isDeviceReq;
          const route = routeForNotification(n);
          const clickable = hasOwnAction || !!route;
          return (
            <Card key={n.id} className={`${!n.read ? "border-primary" : ""}`} data-testid={`notif-${n.id}`}>
              <CardContent className="p-4 flex flex-col md:flex-row justify-between items-start gap-3">
                <div
                  className={`flex gap-3 items-start flex-1 ${clickable && !hasOwnAction ? "cursor-pointer hover:opacity-80" : ""}`}
                  onClick={() => onOpen(n, hasOwnAction)}
                  role="button"
                  tabIndex={0}
                  data-testid={`notif-open-${n.id}`}
                >
                  {icon}
                  <div>
                    <div className="text-sm">{n.message}</div>
                    {!hasOwnAction && route && <div className="text-[11px] text-primary mt-1">Görüntülemek için tıklayın →</div>}
                    {n.reason && <div className="text-xs text-muted-foreground mt-1"><b>Sebep:</b> {n.reason}</div>}
                    {isDeviceReq && n.ip && (
                      <div className="text-xs text-muted-foreground mt-1"><b>IP:</b> <span className="font-mono">{n.ip}</span></div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">{new Date(n.at).toLocaleString("tr-TR")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {isDeviceReq && (
                    <>
                      <Button size="sm" onClick={() => decideDevice(n.approval_id, "approve")} data-testid={`notif-device-approve-${n.id}`}>
                        Ben Onaylıyorum
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => decideDevice(n.approval_id, "deny")} data-testid={`notif-device-deny-${n.id}`}>
                        Reddet
                      </Button>
                    </>
                  )}
                  {isPwdReq && (
                    <Button asChild size="sm" data-testid={`notif-approve-pwd-${n.id}`}>
                      <Link to={`/approve-password-change/${n.token}`}>İmzamla Onayla</Link>
                    </Button>
                  )}
                  {isPwdReset && (
                    <Button asChild size="sm" variant="outline" data-testid={`notif-reset-pwd-${n.id}`}>
                      <Link to={n.reset_url.startsWith("http") ? "#" : n.reset_url}>Şifre Sıfırla</Link>
                    </Button>
                  )}
                  {!n.read && <Badge>Yeni</Badge>}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {items.length === 0 && <div className="text-muted-foreground">Bildirim yok</div>}
      </div>
    </div>
  );
}
